/**
 * ActionsEditorRenderer
 *
 * JSONForms custom renderer for editing business rule actions.
 * Wraps the ActionConfigurator component to integrate with JSONForms.
 *
 * Supported action types:
 * - ValidationError: Block operation with error message
 * - SetField: Set a field value (coming soon)
 * - TriggerCommand: Execute a command (coming soon)
 * - TriggerWorkflow: Start a workflow (coming soon)
 */

import { useMemo } from 'react';
import { rankWith, ControlProps, isControl } from '@jsonforms/core';
import { withJsonFormsControlProps } from '@jsonforms/react';
import { Box } from '@mui/material';
import { ActionConfigurator } from '@/core/rules/components/ActionConfigurator';
import type { RuleActionDto } from '@/types/core/Rules';

/**
 * Tester for the ActionsEditorRenderer
 * Matches the #/properties/actions scope
 */
export const actionsEditorTester = rankWith(
  15, // High priority to override default JSONB renderer
  (uischema) => {
    return isControl(uischema) && uischema.scope === '#/properties/actions';
  }
);

/**
 * ActionsEditorRenderer Component
 */
const ActionsEditorRendererComponent = (props: ControlProps) => {
  const { data, handleChange, path, errors } = props;

  // Parse the actions value
  const actionsValue: RuleActionDto[] = useMemo(() => {
    if (!data) {
      return [];
    }
    try {
      if (Array.isArray(data)) {
        return data;
      }
      return typeof data === 'string' ? JSON.parse(data) : [];
    } catch {
      console.warn('[ActionsEditorRenderer] Failed to parse actions JSON:', data);
      return [];
    }
  }, [data]);

  // Handle actions change - must stringify since schema expects string type
  const handleActionsChange = (actions: RuleActionDto[]) => {
    const jsonString = actions.length > 0 ? JSON.stringify(actions) : null;
    handleChange(path, jsonString);
  };

  return (
    <Box sx={{ py: 1 }}>
      <ActionConfigurator
        value={actionsValue}
        onChange={handleActionsChange}
        error={errors}
        disabled={false}
      />
    </Box>
  );
};

/**
 * Connected renderer with JSON Forms props
 */
export const ActionsEditorRenderer = withJsonFormsControlProps(
  ActionsEditorRendererComponent
);

/**
 * Renderer entry for registration in customRenderers array
 */
export const actionsEditorRendererEntry = {
  tester: actionsEditorTester,
  renderer: ActionsEditorRenderer,
};
